import type { TableProps } from './Table.types';
import TablePagination from './TablePagination';
import TableSorting from './TableSorting';
import { TableVariant } from './constants';
import {
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table';
import { AnimatePresence, motion } from 'framer-motion';
import React, { memo, useState } from 'react';

import { cn } from 'utils/cn';

import withErrorBoundary from 'components/ui/ErrorBoundary/withErrorBoundary';

import * as Table from './Table.components';
import { ChevronIcon } from 'components/Icons';
import Spinner from 'components/Icons/Spinner';

const TableComponent = <TData extends object>({
  columns,
  data,
  isLoading = false,
  placeholder,
  showRowNumbers,
  showPagination,
  paginationConfig,
  sortConfig,
  onRowClick,
  showHeader = true,
  variant = TableVariant.Primary,
  renderRowDetails,
  narrowColumns = false,
  className
}: TableProps<TData>) => {
  const { getRowModel, getHeaderGroups } = useReactTable({
    data,
    columns,
    onSortingChange: sortConfig?.setSorting,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting: sortConfig?.sorting
    }
  });

  const rows = getRowModel().rows;

  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const toggleExpandRow = (rowId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  return (
    <div className={cn('flex flex-col', className)}>
      <Table.TableRoot
        className={cn({
          'bg-blue-800': variant === TableVariant.Primary,
          'bg-blue-900': variant === TableVariant.Secondary
        })}
      >
        {showHeader && (
          <Table.TableHeader>
            {getHeaderGroups().map((headerGroup) => (
              <Table.TableRow key={headerGroup.id} className='bg-blue-900'>
                {showRowNumbers && (
                  <Table.TableHead
                    className={cn('w-10 py-2.5 text-center text-xs leading-5', {
                      'px-2.5': !narrowColumns,
                      'px-1': narrowColumns
                    })}
                  >
                    #
                  </Table.TableHead>
                )}

                {headerGroup.headers.map((header) => {
                  const className = header.column.columnDef?.meta?.className;
                  const isSortable = header.column.getCanSort();

                  return (
                    <Table.TableHead
                      key={header.id}
                      className={cn('py-2.5 text-xs leading-5', className, {
                        'px-2.5': !narrowColumns,
                        'px-1': narrowColumns
                      })}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}

                      {isSortable && <TableSorting column={header.column} />}
                    </Table.TableHead>
                  );
                })}

                {renderRowDetails && (
                  <Table.TableHead
                    className={cn('w-4 py-2.5', {
                      'px-2.5': !narrowColumns,
                      'px-1': narrowColumns
                    })}
                  />
                )}
              </Table.TableRow>
            ))}
          </Table.TableHeader>
        )}

        <Table.TableBody>
          {!isLoading &&
            !!rows.length &&
            rows.map((row, rowIndex) => {
              const rowNumber =
                showPagination && paginationConfig
                  ? rowIndex +
                    1 +
                    (paginationConfig.currentPage - 1) *
                      paginationConfig.resultsPerPage
                  : rowIndex + 1;

              return (
                <React.Fragment key={row.id}>
                  <Table.TableRow
                    onClick={() => {
                      onRowClick?.(row.original);
                    }}
                    className={cn('relative z-11 text-white shadow-table', {
                      'bg-blue-900': variant === TableVariant.Primary,
                      'bg-blue-800': variant === TableVariant.Secondary,
                      'cursor-pointer transition-colors duration-300':
                        onRowClick,
                      'hover:bg-blue-900/70':
                        onRowClick && variant === TableVariant.Primary,
                      'hover:bg-blue-800/70':
                        onRowClick && variant === TableVariant.Secondary
                    })}
                  >
                    {showRowNumbers && (
                      <Table.TableCell
                        className={cn(
                          'whitespace-nowrap py-2.5 text-center text-xs leading-5',
                          {
                            'px-2.5': !narrowColumns,
                            'px-1': narrowColumns
                          }
                        )}
                      >
                        {rowNumber}
                      </Table.TableCell>
                    )}

                    {row.getVisibleCells().map((cell) => {
                      const className = cell.column.columnDef?.meta?.className;

                      return (
                        <Table.TableCell
                          key={cell.id}
                          className={cn(
                            'whitespace-nowrap py-2.5 text-xs leading-5',
                            className,
                            {
                              'px-2.5': !narrowColumns,
                              'px-1': narrowColumns
                            }
                          )}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </Table.TableCell>
                      );
                    })}

                    {renderRowDetails && (
                      <Table.TableCell className='px-0'>
                        <div
                          className='flex size-5 cursor-pointer items-center justify-center rounded-full transition-colors duration-300 hover:bg-blue-200/20'
                          onClick={() => toggleExpandRow(row.id)}
                        >
                          <ChevronIcon
                            className={cn(
                              'size-2 stroke-blue-200 transition-transform',
                              {
                                'rotate-180': expandedRows[row.id]
                              }
                            )}
                          />
                        </div>
                      </Table.TableCell>
                    )}
                  </Table.TableRow>

                  {renderRowDetails && (
                    <Table.TableRow>
                      <Table.TableCell
                        className={cn({
                          'px-2.5': !narrowColumns,
                          'px-1': narrowColumns
                        })}
                        colSpan={columns.length + 1}
                      >
                        <AnimatePresence>
                          {expandedRows[row.id] && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.5 }}
                              className='-ml-2.5 -mr-2.5 -mt-4 w-auto overflow-hidden rounded-b-lg'
                            >
                              {renderRowDetails?.(row.original)}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </Table.TableCell>
                    </Table.TableRow>
                  )}
                </React.Fragment>
              );
            })}

          {!isLoading && !rows.length && (
            <Table.TableRow>
              <Table.TableCell
                colSpan={showRowNumbers ? columns.length + 1 : columns.length}
                className='h-24 p-0'
              >
                <div
                  className={cn(
                    'flex h-full w-full items-center justify-center rounded-lg text-center text-white',
                    {
                      'bg-blue-900': variant === TableVariant.Primary,
                      'bg-blue-800': variant === TableVariant.Secondary
                    }
                  )}
                >
                  {placeholder}
                </div>
              </Table.TableCell>
            </Table.TableRow>
          )}

          {isLoading && (
            <Table.TableRow>
              <Table.TableCell
                colSpan={showRowNumbers ? columns.length + 1 : columns.length}
                className='h-24 p-0'
              >
                <div
                  className={cn(
                    'flex h-full w-full items-center justify-center rounded-lg',
                    {
                      'bg-blue-900': variant === TableVariant.Primary,
                      'bg-blue-800': variant === TableVariant.Secondary
                    }
                  )}
                >
                  <Spinner className='h-10 w-10 stroke-gray-100' />
                </div>
              </Table.TableCell>
            </Table.TableRow>
          )}
        </Table.TableBody>
      </Table.TableRoot>

      {showPagination && paginationConfig && (
        <TablePagination {...paginationConfig} />
      )}
    </div>
  );
};

export default memo(withErrorBoundary(TableComponent)) as typeof TableComponent;
